import pino from 'pino'

import { DEMOTED_ERROR_MESSAGES, installGlobalErrorHandlers, type LogSink } from './globalHandlers'
import { createBatchedLogTransport } from './loggerTransport'

export { DEMOTED_ERROR_MESSAGES } from './globalHandlers'

export interface CreateLoggerOptions {
    avenProject: string
    logProcessorBaseUrl?: string
    clientSideLogsEnabled: boolean
    getContext?: () => Record<string, string>
    batch?: {
        maxBatchSize?: number
        flushIntervalMs?: number
        maxQueueSize?: number
    }
}

type LogMethod = (template: string, args?: Record<string, unknown>) => void

export interface Logger {
    debug: LogMethod
    info: LogMethod
    warn: LogMethod
    error: LogMethod
    fatal: LogMethod
    flushLogs: () => Promise<void>
    /** Wire a Vue app's `config.errorHandler` into this logger (Vue 2 or Vue 3). */
    setupVueErrorHandler: (appOrVue: unknown) => void
}

function normalizeErrors(args: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(args)) {
        out[k] = v instanceof Error ? pino.stdSerializers.err(v) : v
    }
    return out
}

export function createLogger(options: CreateLoggerOptions): Logger {
    const transport = createBatchedLogTransport({
        logProcessorBaseUrl: options.logProcessorBaseUrl,
        avenProject: options.avenProject,
        getContext: options.getContext,
        clientSideLogsEnabled: options.clientSideLogsEnabled,
        maxBatchSize: options.batch?.maxBatchSize,
        flushIntervalMs: options.batch?.flushIntervalMs,
        maxQueueSize: options.batch?.maxQueueSize,
    })

    const emit = (levelLabel: string, template: string, args?: Record<string, unknown>): void => {
        if (options.clientSideLogsEnabled) {
            // eslint-disable-next-line no-console
            console.log(levelLabel, template, args ?? {})
        }
        transport.enqueue({
            levelLabel,
            timestamp: Date.now(),
            template,
            args: normalizeErrors(args ?? {}),
        })
    }

    return {
        debug: (t, a) => emit('DEBUG', t, a),
        info: (t, a) => emit('INFO', t, a),
        warn: (t, a) => emit('WARN', t, a),
        error: (t, a) => emit('ERROR', t, a),
        fatal: (t, a) => emit('FATAL', t, a),
        flushLogs: transport.flush.bind(transport),
        setupVueErrorHandler: (appOrVue) => {
            const sink: LogSink = {
                info: (t, a) => emit('INFO', t, a),
                warn: (t, a) => emit('WARN', t, a),
                error: (t, a) => emit('ERROR', t, a),
            }
            installGlobalErrorHandlers(sink)

            if (!appOrVue || typeof appOrVue !== 'object' || !('config' in appOrVue)) {
                return
            }
            const config = appOrVue.config
            if (!config || typeof config !== 'object') {
                return
            }
            // Vue app/constructor exposure of config.errorHandler (Vue 2 or Vue 3) — structural boundary.
            const vueConfig = config as { errorHandler?: (err: unknown) => void }
            vueConfig.errorHandler = (err: unknown) => {
                const isError = err instanceof Error
                const args = { error: err, errorStack: isError ? err.stack : undefined }
                if (DEMOTED_ERROR_MESSAGES.some((re) => re.test(String(isError ? err.message : err)))) {
                    emit('WARN', 'vue error: %{error}\t%{errorStack}', args)
                } else {
                    emit('ERROR', 'vue error: %{error}\t%{errorStack}', args)
                }
            }
        },
    }
}
